import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import {
  doGetAccessibleMissions,
  u3204NormalizeMissionRef,
  type U3204DefaultPromptRef,
  type U3204MissionRef,
  type U3204ModelRef,
  type U3204ProviderRef,
} from '../U3204Service';
import { NS } from '../U3204';
import { luHandleAppiError } from '../U3204Service';
import { u3204BuildAccessibleMissionComboLabel } from '../utils/u3204BuildAccessibleMissionComboLabel';

export interface U3204AccessibleMissionOption extends Record<string, unknown> {
  id: string;
  code: string;
  title: string;
  label: string;
  defaultProvider: U3204ProviderRef | null;
  defaultModel: U3204ModelRef | null;
  defaultPrompts: U3204DefaultPromptRef[];
}

export function useU3204AccessibleMissionsHandlers() {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { t } = useTranslation(NS);
  const toast = useToast();
  const [missions, setMissions] = useState<U3204AccessibleMissionOption[]>([]);
  const [loading, setLoading] = useState(false);

  const loadAccessibleMissions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await doGetAccessibleMissions(u2000AxiosAuthenticated);
      const rows: U3204MissionRef[] = response.data?.results ?? [];
      const options = rows.map((row) => {
        const normalized = u3204NormalizeMissionRef(row);
        const comboLabel = u3204BuildAccessibleMissionComboLabel({
          title: normalized.title,
          code: normalized.code,
        });
        return {
          id: normalized.id,
          code: normalized.code,
          title: normalized.title,
          label: comboLabel,
          defaultProvider: normalized.defaultProvider,
          defaultModel: normalized.defaultModel,
          defaultPrompts: normalized.defaultPrompts,
        };
      });
      setMissions(options);
      return options;
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      return [];
    } finally {
      setLoading(false);
    }
  }, [u2000AxiosAuthenticated, t, toast]);

  return { missions, loading, hasAccessibleMissions: missions.length > 0, loadAccessibleMissions };
}
